import { USDC_DECIMALS } from "@breakout/sdk"
import { TRADING } from "@breakout/shared/constants"
import { Drawer } from "@breakout/ui/components/BottomSheet"
import { Button } from "@breakout/ui/components/Button"
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@breakout/ui/components/DropdownMenu"
import { cn, formatUsd } from "@breakout/ui/utils"
import { Link } from "@tanstack/react-router"
import { ChevronDown, Minus, Plus } from "lucide-react"
import { useCallback, useEffect, useRef, useState } from "react"
import { formatUnits } from "viem"

import { BINARY_LABELS } from "../../lib/constants"
import type { Creator, OutcomeBucket } from "../../lib/types"
import { SimpleAvatar } from "../ui/SimpleAvatar"

type TradeMode = "buy" | "sell"

interface MarketSheetProps {
	// Required - core functionality
	isOpen: boolean
	onClose: () => void
	buckets: OutcomeBucket[]
	onBuy: (bucketId: string, amount: number) => Promise<void>
	onSell: (bucketId: string, amount: number) => Promise<void>
	marketTitle: string
	userShares: Record<string, number>
	userBalanceRaw: bigint | null
	creator: Creator | null

	// Optional - pre-population (sensible defaults exist)
	/** Pre-select an outcome when opening (null = first bucket) */
	initialBucketId?: string | null
	/** Pre-select trade mode when opening (default: "buy") */
	initialTradeMode?: TradeMode
	/** Pre-fill amount when opening (default: 0) */
	initialAmount?: number

	// Optional - UI flags with false defaults
	isLoading?: boolean
	showAddCash?: boolean
	isMarketEnded?: boolean
	/** Required when showAddCash is true */
	onAddCash?: () => void
}

export function MarketSheet({
	isOpen,
	onClose,
	buckets,
	onBuy,
	onSell,
	marketTitle,
	userShares,
	userBalanceRaw,
	creator,
	initialBucketId,
	initialTradeMode,
	initialAmount,
	isLoading = false,
	showAddCash = false,
	isMarketEnded = false,
	onAddCash,
}: MarketSheetProps) {
	// Local state
	const [selectedBucket, setSelectedBucket] = useState<OutcomeBucket | null>(null)
	const [amount, setAmount] = useState(0)
	const [isSubmitting, setIsSubmitting] = useState(false)
	const [tradeMode, setTradeMode] = useState<TradeMode>("buy")

	// Effects
	// Set initial values when sheet opens
	useEffect(() => {
		if (isOpen) {
			if (initialBucketId) {
				const bucket = buckets.find(b => b.id === initialBucketId)
				if (bucket) {
					setSelectedBucket(bucket)
				}
			}
			if (initialTradeMode) {
				setTradeMode(initialTradeMode)
			}
			if (initialAmount != null) {
				setAmount(initialAmount)
			}
		}
	}, [isOpen, initialBucketId, initialTradeMode, initialAmount, buckets])

	// Reset state when sheet closes
	useEffect(() => {
		if (!isOpen) {
			setSelectedBucket(null)
			setAmount(0)
			setTradeMode("buy")
		}
	}, [isOpen])

	// Derived values
	const userSharesForBucket = selectedBucket ? (userShares[selectedBucket.id] ?? 0) : 0
	const priceInCents = selectedBucket ? Math.round(selectedBucket.probability * 100) : 50
	const avgPrice = priceInCents / 100

	// Calculate potential win (shares you'd get = amount / price)
	const sharesToGet = amount > 0 && avgPrice > 0 ? amount / avgPrice : 0
	// Each share pays $1 if correct, so profit = shares - cost
	const potentialWin = sharesToGet

	// Check if user has insufficient funds for buy
	const userBalanceHuman =
		userBalanceRaw != null ? Number(formatUnits(userBalanceRaw, USDC_DECIMALS)) : 0
	const isInsufficientFunds = tradeMode === "buy" && amount > 0 && amount > userBalanceHuman
	const isBelowMinimum = tradeMode === "buy" && amount > 0 && amount < TRADING.MIN_BUY_AMOUNT
	// Check if user has insufficient shares for sell
	const isInsufficientShares = tradeMode === "sell" && amount > 0 && amount > userSharesForBucket

	const canTrade =
		!isMarketEnded && (tradeMode === "buy" ? !!onBuy : !!onSell && userSharesForBucket > 0)
	const isUpDirection = selectedBucket?.direction === "up"

	// Event handlers
	const handleConfirm = async () => {
		if (!selectedBucket || amount <= 0) {
			return
		}
		if (tradeMode === "buy" && !onBuy) {
			return
		}
		if (tradeMode === "sell" && !onSell) {
			return
		}
		setIsSubmitting(true)
		try {
			if (tradeMode === "buy" && onBuy) {
				await onBuy(selectedBucket.id, amount)
			} else if (tradeMode === "sell" && onSell) {
				await onSell(selectedBucket.id, amount)
			}
			await new Promise(resolve => setTimeout(resolve, 500))
			onClose()
		} catch {
			// Error already shown via toast in parent
		} finally {
			setIsSubmitting(false)
		}
	}

	const amountInputRef = useRef<HTMLInputElement>(null)

	const incrementAmount = useCallback((delta: number) => {
		setAmount(prev => Math.max(0, prev + delta))
	}, [])

	const handleAmountInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		const raw = e.target.value.replace(/[^0-9]/g, "")
		setAmount(raw === "" ? 0 : Number.parseInt(raw, 10))
	}, [])

	// Don't render if no creator or bucket selected
	if (!creator || !selectedBucket) {
		return null
	}

	return (
		<Drawer.Root
			onOpenChange={open => {
				if (!open) {
					onClose()
				}
			}}
			open={isOpen}
		>
			<Drawer.Portal>
				<Drawer.Overlay className="fixed inset-0 z-[var(--z-modal-backdrop)] bg-black/60 backdrop-blur-sm" />
				<Drawer.Content
					className="fixed inset-x-0 bottom-0 z-[var(--z-modal)] mx-auto w-full max-w-lg overscroll-contain rounded-t-3xl bg-bg-card pb-safe shadow-2xl outline-none ring-1 ring-border-subtle"
					data-handle={creator.handle}
					data-testid="market-sheet"
				>
					{/* Handle bar */}
					<div className="flex justify-center pt-3 pb-2">
						<Drawer.Handle className="h-1.5 w-12 rounded-full bg-bg-tertiary" />
					</div>

					{/* Content */}
					<div className="px-4 pb-6">
						{isLoading ? (
							<output
								aria-label="Loading"
								className="flex flex-col items-center justify-center gap-3 py-12"
							>
								<span
									aria-hidden="true"
									className="block size-6 animate-spin rounded-full border-2 border-breakout-brand border-t-transparent"
								/>
								<p className="font-medium text-sm text-text-muted">Loading details…</p>
							</output>
						) : (
							<>
								{/* Top row: Buy/Sell dropdown + Market label */}
								<div className="mb-4 flex items-center justify-between">
									<DropdownMenu modal={false}>
										<DropdownMenuTrigger asChild>
											<button
												aria-label={`Trade mode: ${tradeMode}. Click to change.`}
												className="flex cursor-pointer items-center gap-1.5 rounded-lg border border-border bg-bg-secondary px-3 py-1.5 font-semibold text-text-primary transition-all hover:bg-bg-tertiary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-breakout-brand active:scale-95"
												type="button"
											>
												{tradeMode === "buy" ? "Buy" : "Sell"}
												<ChevronDown aria-hidden="true" className="size-4 text-text-muted" />
											</button>
										</DropdownMenuTrigger>
										<DropdownMenuContent
											align="start"
											className="min-w-[100px] rounded-xl border border-border bg-bg-card p-1 shadow-xl"
										>
											<DropdownMenuItem
												className={cn(
													"cursor-pointer rounded-md px-3 py-2 font-medium text-sm transition-colors",
													tradeMode === "buy"
														? "bg-bg-secondary text-text-primary"
														: "text-text-muted hover:bg-bg-secondary hover:text-text-primary",
												)}
												onClick={() => setTradeMode("buy")}
											>
												Buy
											</DropdownMenuItem>
											<DropdownMenuItem
												className={cn(
													"cursor-pointer rounded-md px-3 py-2 font-medium text-sm transition-colors",
													tradeMode === "sell"
														? "bg-bg-secondary text-text-primary"
														: "text-text-muted hover:bg-bg-secondary hover:text-text-primary",
												)}
												disabled={userSharesForBucket === 0}
												onClick={() => setTradeMode("sell")}
											>
												Sell
											</DropdownMenuItem>
										</DropdownMenuContent>
									</DropdownMenu>
									{/* Hidden title for accessibility */}
									<Drawer.Title className="sr-only">Trade</Drawer.Title>
								</div>

								{/* Market context row */}
								<div className="mb-6 flex items-center gap-3">
									<SimpleAvatar
										alt={creator.name}
										className="shrink-0"
										size="md"
										src={creator.avatarUrl}
										variant="square"
									/>
									<div className="min-w-0 flex-1">
										<p className="truncate font-bold text-base text-text-primary">
											{marketTitle ?? `Where will @${creator.handle} rank?`}
										</p>
										<div className="mt-0.5 flex items-center gap-2">
											<span className="truncate font-medium text-text-muted text-xs">
												{selectedBucket.label}
											</span>
											<span
												className={`rounded px-1.5 py-0.5 font-medium text-xs ${
													isUpDirection
														? "bg-text-positive/10 text-text-positive"
														: "bg-text-negative/10 text-text-negative"
												}`}
											>
												{isUpDirection ? BINARY_LABELS[0] : BINARY_LABELS[1]}
											</span>
										</div>
									</div>
									<div className="text-right font-medium text-text-muted text-xs">
										<span>Bal.</span>
										<span className="ml-1 font-bold font-mono text-text-primary">
											${userSharesForBucket.toFixed(2)}
										</span>
									</div>
								</div>

								{/* Amount stepper with tappable input */}
								<div className="mb-6 flex items-center justify-center gap-6">
									<button
										aria-label="Decrease amount by $10"
										className="flex size-14 cursor-pointer items-center justify-center rounded-2xl border border-border bg-bg-secondary text-text-muted transition-all hover:scale-105 hover:border-border-highlight hover:bg-bg-tertiary hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-breakout-brand active:scale-95"
										onClick={() => incrementAmount(-10)}
										type="button"
									>
										<Minus aria-hidden="true" className="size-6" />
									</button>
									<div className="relative min-w-[120px] text-center">
										<span className="pointer-events-none absolute inset-y-0 left-0 flex items-center justify-center pl-1 font-bold text-5xl text-text-primary">
											$
										</span>
										<input
											ref={amountInputRef}
											aria-label="Trade amount in dollars"
											autoComplete="off"
											className="w-full bg-transparent text-center font-bold text-5xl text-text-primary tabular-nums caret-breakout-brand outline-none"
											inputMode="numeric"
											onChange={handleAmountInputChange}
											pattern="[0-9]*"
											style={{ paddingLeft: "1.25rem" }}
											value={amount || ""}
										/>
									</div>
									<button
										aria-label="Increase amount by $10"
										className="flex size-14 cursor-pointer items-center justify-center rounded-2xl border border-border bg-bg-secondary text-text-muted transition-all hover:scale-105 hover:border-border-highlight hover:bg-bg-tertiary hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-breakout-brand active:scale-95"
										onClick={() => incrementAmount(10)}
										type="button"
									>
										<Plus aria-hidden="true" className="size-6" />
									</button>
								</div>

								{/* Max payout estimate */}
								{amount > 0 && tradeMode === "buy" && (
									<div className="mb-4 text-center">
										<div className="inline-block rounded-xl bg-green-500/10 px-6 py-3">
											<p className="font-bold text-2xl text-green-500 tabular-nums">
												Max Payout: {formatUsd(potentialWin)}
											</p>
											<p className="mt-1 font-medium text-text-muted text-xs">
												~{sharesToGet.toFixed(2)} shares
											</p>
										</div>
									</div>
								)}

								{/* Sell mode: show shares owned */}
								{tradeMode === "sell" && (
									<div className="mb-4 text-center">
										{userSharesForBucket > 0 ? (
											<p className="text-sm text-text-muted">
												You own{" "}
												<span className="font-bold text-text-primary">
													{userSharesForBucket.toFixed(2)}
												</span>{" "}
												shares
											</p>
										) : (
											<p className="text-sm text-text-negative">You don't own any shares</p>
										)}
									</div>
								)}

								{/* Quick amount buttons */}
								<div className="mb-6 flex justify-center gap-2">
									{tradeMode === "buy"
										? ([25, 50, 75, 100] as const).map(pct => {
												// Calculate amount in bigint to avoid JS floating point issues
												const calculatedAmountRaw =
													userBalanceRaw != null
														? pct === 100
															? userBalanceRaw // Full balance, no math needed
															: (userBalanceRaw * BigInt(pct)) / 100n // Bigint division floors automatically
														: 0n
												// Convert to human-readable number (USDC has 6 decimals)
												const calculatedAmount =
													userBalanceRaw != null
														? Number(formatUnits(calculatedAmountRaw, USDC_DECIMALS))
														: 0
												const isDisabled = userBalanceRaw == null || userBalanceRaw === 0n
												return (
													<button
														aria-label={`Set amount to ${pct}% of balance`}
														className="min-h-[44px] min-w-[44px] cursor-pointer rounded-xl border border-border bg-bg-secondary px-4 py-2 font-medium text-sm text-text-secondary transition-all hover:border-border-highlight hover:bg-bg-tertiary hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-breakout-brand active:scale-95 disabled:cursor-not-allowed disabled:opacity-50"
														disabled={isDisabled}
														key={pct}
														onClick={() => setAmount(calculatedAmount)}
														type="button"
													>
														{pct === 100 ? "Max" : `${pct}%`}
													</button>
												)
											})
										: ([25, 50, 75, 100] as const).map(pct => {
												const sellAmount =
													pct === 100
														? Math.floor(userSharesForBucket)
														: Math.floor((userSharesForBucket * pct) / 100)
												return (
													<button
														aria-label={`Sell ${pct}% of shares`}
														className="min-h-[44px] min-w-[44px] cursor-pointer rounded-xl border border-border bg-bg-secondary px-4 py-2 font-medium text-sm text-text-secondary transition-all hover:border-border-highlight hover:bg-bg-tertiary hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-breakout-brand active:scale-95 disabled:cursor-not-allowed disabled:opacity-50"
														disabled={userSharesForBucket === 0}
														key={pct}
														onClick={() => setAmount(sellAmount)}
														type="button"
													>
														{pct === 100 ? "Max" : `${pct}%`}
													</button>
												)
											})}
								</div>

								{/* Action button - Shows "Add cash" if insufficient funds or zero balance */}
								{(showAddCash || isInsufficientFunds) && tradeMode === "buy" && onAddCash ? (
									<button
										className="min-h-[48px] w-full cursor-pointer rounded-xl border border-breakout-brand bg-breakout-brand py-3.5 font-semibold text-base text-white transition-all hover:bg-breakout-brand-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-breakout-brand focus-visible:ring-offset-2 active:scale-[0.98]"
										onClick={onAddCash}
										type="button"
									>
										Add cash
									</button>
								) : (
									<Button
										className="w-full"
										disabled={
											isMarketEnded ||
											amount <= 0 ||
											isSubmitting ||
											!canTrade ||
											isBelowMinimum ||
											isInsufficientShares
										}
										loading={isSubmitting}
										onClick={handleConfirm}
										size="lg"
										variant={tradeMode === "buy" ? "primary" : "danger"}
									>
										{isMarketEnded
											? "Market Ended"
											: isSubmitting
												? "Processing…"
												: isBelowMinimum
													? "Minimum $1"
													: isInsufficientShares
														? "Insufficient Shares"
														: tradeMode === "buy"
															? amount > 0
																? `Buy ${selectedBucket.label}`
																: "Enter amount"
															: amount > 0
																? `Sell ${amount} shares`
																: "Enter amount"}
									</Button>
								)}

								{/* Terms link */}
								<p className="mt-3 text-center text-text-muted text-xs">
									By trading, you agree to the{" "}
									<Link className="underline" to="/terms">
										Terms of Use
									</Link>
									.
								</p>
							</>
						)}
					</div>
				</Drawer.Content>
			</Drawer.Portal>
		</Drawer.Root>
	)
}
